/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { ChannelResource, ProjectResource, LifecycleResource, Repository } from "@octopusdeploy/octopus-server-client";
import { OctopusError, Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as _ from "lodash";
import { useMemo } from "react";
import { useTrackSaveChannelEvent } from "~/areas/projects/components/Channels/SaveChannelEvent";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { useForm } from "~/components/form/Form/Form";

const inheritFromProjectLifecycleId = "-1";

export function useChannelForm(project: ProjectResource, channel: ChannelResource | "create new channel", lifecycles: LifecycleResource[], formName: string, onSuccess?: (channel: ChannelResource) => void) {
    const { navigate } = useSpaceAwareNavigation();
    const isCreatingNewChannel = channel === "create new channel";

    const initialModel =
        channel === "create new channel"
            ? ({
                  Id: null!,
                  ProjectId: project.Id,
                  SpaceId: "",
                  Name: "",
                  Description: "",
                  IsDefault: false,
                  LifecycleId: null!,
                  Rules: [],
                  TenantTags: [],
                  Links: null!,
                  GitReferenceRules: [],
                  GitResourceRules: [],
                  CustomFieldDefinitions: [],
                  Type: "Lifecycle",
                  EphemeralEnvironmentNameTemplate: null,
                  ParentEnvironmentId: null,
                  AutomaticEphemeralEnvironmentDeployments: null,
              } satisfies ChannelResource)
            : channel;

    const trackSaveChannelEvent = useTrackSaveChannelEvent();
    const form = useForm({
        formName,
        initialModel,
        snackbarSaveText: isCreatingNewChannel ? "Channel created" : "Channel details updated",
        submitPermission: {
            permission: Permission.ProcessEdit,
            project: project.Id,
        },
        onSubmit: async (repository: Repository, channelModel) => {
            if (channelModel.Type === "Lifecycle") {
                validateLifecycle(lifecycleOptions);
            }

            const savedChannel = await repository.Channels.saveToProject(project, channelModel);
            return savedChannel;
        },
        updateFormModelAfterSubmit: (model) => model,
        onSubmitError: (error) => trackSaveChannelEvent(project.Id, error),
        afterSubmit: async (_, updatedChannel) => {
            onSuccess?.(updatedChannel);
            trackSaveChannelEvent(project.Id);
            if (isCreatingNewChannel) {
                navigate(links.channelsPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug }));
            }
        },
    });

    const validateLifecycle = (lifecycleOptions: LifecycleResource[]) => {
        const lifecycleId = form.model.LifecycleId || inheritFromProjectLifecycleId;

        if (!lifecycleOptions.some((option) => option.Id === lifecycleId)) {
            const exception = new OctopusError(0, "There were validation errors:");
            exception.Errors = ["Select a lifecycle"];
            throw exception;
        }
    };

    const lifecycleOptions: LifecycleResource[] = useMemo(() => {
        const sortedLifecycles = _.sortBy(lifecycles, (l) => l.Name);

        return [
            {
                Name: "Inherit from project",
                Id: inheritFromProjectLifecycleId,
                SpaceId: project.SpaceId,
                ReleaseRetentionPolicy: null!,
                TentacleRetentionPolicy: null!,
                Phases: [],
                Links: null!,
            },
            ...sortedLifecycles,
        ];
    }, [project.SpaceId, lifecycles]);

    return { ...form, lifecycleOptions };
}
